import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper, Grid, Button } from '@mui/material';
import gsap from 'gsap';

/**
 * @component TradingProcessAnimation
 * @description Animated visualization of the minerals trading process
 *
 * SEO Considerations:
 * - Provides text alternatives for animation content
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Includes keyword-rich content for mineral trading process
 * - Ensures content is accessible even when JavaScript is disabled
 * - Enhances user experience while maintaining SEO-friendly content
 */
const TradingProcessAnimation = () => {
  // References for animation elements
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const step4Ref = useRef(null);
  const step5Ref = useRef(null);
  const mineralRef = useRef(null);
  const documentRef = useRef(null);
  const verificationRef = useRef(null);
  const paymentRef = useRef(null);
  const shippingRef = useRef(null);

  // Animation will run continuously without manual controls

  /**
   * Initialize animation with SEO considerations
   * - Animation enhances user experience but doesn't hide essential content
   * - All key information is available in text form for search engines
   * - Provides visual representation of the ethical mineral trading process
   */
  useEffect(() => {
    // Initial state
    gsap.set([step1Ref.current, step2Ref.current, step3Ref.current, step4Ref.current, step5Ref.current], {
      opacity: 0.3,
      scale: 0.95
    });

    gsap.set(step1Ref.current, {
      opacity: 1,
      scale: 1
    });

    gsap.set([mineralRef.current, documentRef.current, verificationRef.current, paymentRef.current, shippingRef.current], {
      autoAlpha: 0,
      y: 20
    });

    gsap.set(mineralRef.current, {
      autoAlpha: 1,
      y: 0
    });

    // Create timeline
    const tl = gsap.timeline({
      repeat: -1,
      repeatDelay: 1,
      defaults: { duration: 1, ease: "power2.inOut" }
    });

    // Step 1: Miner lists minerals
    tl.addLabel("step1")
      .to(mineralRef.current, {
        x: "+=50",
        duration: 1.5,
        ease: "power1.inOut"
      })

      // Step 2: Documentation & verification
      .addLabel("step2")
      .to(step1Ref.current, {
        opacity: 0.3,
        scale: 0.95
      })
      .to(step2Ref.current, {
        opacity: 1,
        scale: 1
      }, "<")
      .to(documentRef.current, {
        autoAlpha: 1,
        y: 0
      }, "<")
      .to(mineralRef.current, {
        x: "+=50"
      })
      .to(documentRef.current, {
        x: "+=50"
      }, "<")

      // Step 3: Verification
      .addLabel("step3")
      .to(step2Ref.current, {
        opacity: 0.3,
        scale: 0.95
      })
      .to(step3Ref.current, {
        opacity: 1,
        scale: 1
      }, "<")
      .to(verificationRef.current, {
        autoAlpha: 1,
        y: 0
      }, "<")
      .to([mineralRef.current, documentRef.current], {
        x: "+=50"
      })
      .to(verificationRef.current, {
        x: "+=50"
      }, "<")

      // Step 4: Payment
      .addLabel("step4")
      .to(step3Ref.current, {
        opacity: 0.3,
        scale: 0.95
      })
      .to(step4Ref.current, {
        opacity: 1,
        scale: 1
      }, "<")
      .to(paymentRef.current, {
        autoAlpha: 1,
        y: 0
      }, "<")
      .to([mineralRef.current, documentRef.current, verificationRef.current], {
        x: "+=50"
      })
      .to(paymentRef.current, {
        x: "+=50"
      }, "<")

      // Step 5: Shipping & Delivery
      .addLabel("step5")
      .to(step4Ref.current, {
        opacity: 0.3,
        scale: 0.95
      })
      .to(step5Ref.current, {
        opacity: 1,
        scale: 1
      }, "<")
      .to(shippingRef.current, {
        autoAlpha: 1,
        y: 0
      }, "<")
      .to([mineralRef.current, documentRef.current, verificationRef.current, paymentRef.current], {
        x: "+=50"
      })
      .to(shippingRef.current, {
        x: "+=50"
      }, "<")

      // Complete cycle
      .to([mineralRef.current, documentRef.current, verificationRef.current, paymentRef.current, shippingRef.current], {
        autoAlpha: 0,
        stagger: 0.1
      }, "+=1")
      .to([step1Ref.current, step2Ref.current, step3Ref.current, step4Ref.current, step5Ref.current], {
        opacity: 0.3,
        scale: 0.95
      }, "<")
      .to(step1Ref.current, {
        opacity: 1,
        scale: 1
      }, "<")
      .set([mineralRef.current, documentRef.current, verificationRef.current, paymentRef.current, shippingRef.current], {
        x: 0
      })
      .set(mineralRef.current, {
        autoAlpha: 1,
        y: 0
      });

    // Store timeline reference and auto-play on mount
    timelineRef.current = tl;

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <Paper
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 3,
        border: '2px solid #f5e5a5',
        borderLeft: '6px solid #eab308',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        background: 'linear-gradient(to right, #fffef0, #fffde7)',
        p: 3,
        mb: 4,
        overflow: 'hidden'
      }}
    >
      <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Minerals Trading Process
      </Typography>

      {/* Process steps */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            ref={step1Ref}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: '1px solid #f5e5a5',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              1. Listing
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Miner lists minerals with certificates
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            ref={step2Ref}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: '1px solid #f5e5a5',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              2. Documentation
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Origin and quality documentation
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            ref={step3Ref}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: '1px solid #f5e5a5',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              3. Verification
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Blockchain verification of claims
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            ref={step4Ref}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: '1px solid #f5e5a5',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              4. Payment
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Secure escrow payment processing
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            ref={step5Ref}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: '1px solid #f5e5a5',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              5. Delivery
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Tracked shipping and delivery
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Animation track */}
      <Box
        sx={{
          position: 'relative',
          height: 100,
          bgcolor: 'rgba(234, 179, 8, 0.1)',
          borderRadius: 2,
          mb: 2,
          overflow: 'hidden'
        }}
      >
        {/* Track line */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 4,
            bgcolor: '#eab308',
            transform: 'translateY(-50%)',
            zIndex: 0
          }}
        />

        {/* Animated elements */}
        <Box
          ref={mineralRef}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 50,
            transform: 'translateY(-50%)',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: '#4caf50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            💎
          </Typography>
        </Box>

        <Box
          ref={documentRef}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 50,
            transform: 'translateY(-50%)',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: '#2196f3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            📄
          </Typography>
        </Box>

        <Box
          ref={verificationRef}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 50,
            transform: 'translateY(-50%)',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: '#9c27b0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            ✓
          </Typography>
        </Box>

        <Box
          ref={paymentRef}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 50,
            transform: 'translateY(-50%)',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: '#ff9800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            💰
          </Typography>
        </Box>

        <Box
          ref={shippingRef}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 50,
            transform: 'translateY(-50%)',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: '#f44336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            🚚
          </Typography>
        </Box>
      </Box>

      {/* Animation runs automatically in a continuous loop */}

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
          mt: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
          <Typography variant="caption">Minerals</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3' }} />
          <Typography variant="caption">Documents</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9c27b0' }} />
          <Typography variant="caption">Verification</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
          <Typography variant="caption">Payments</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} />
          <Typography variant="caption">Shipping</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default TradingProcessAnimation;
